import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

export interface StatTileProps {
  label: string;
  value: string;
  /** Optional control rendered under the value (the UW Override Multiplier input). */
  slot?: ReactNode;
  emphasis?: boolean;
  /** The number is stale while the engine answers a save: dimmed with a spinner, never blanked, so nothing jumps. */
  recalculating?: boolean;
  children?: never;
}

export function StatTile({ label, value, slot, emphasis = false, recalculating = false }: StatTileProps) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-md" aria-busy={recalculating}>
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
        {recalculating && <Loader2 size={12} className="animate-spin text-brand-green" aria-label="Recalculating" />}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums transition-opacity ${recalculating ? "opacity-50" : ""} ${emphasis ? "text-brand-green-dark" : "text-brand-navy"}`}>{value}</p>
      {slot && <div className="mt-2">{slot}</div>}
    </div>
  );
}

interface StatStripProps {
  tiles: readonly StatTileProps[];
  ariaLabel: string;
  /** Applies to every tile: the totals are being recalculated by the API after a save. */
  recalculating?: boolean;
}

const WIDE_COLUMNS: Record<number, string> = {
  1: "xl:grid-cols-1",
  2: "xl:grid-cols-2",
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
};

/** Stats row: one line on wide screens, wrapping to two on narrow ones. */
export function StatStrip({ tiles, ariaLabel, recalculating = false }: StatStripProps) {
  const wide = WIDE_COLUMNS[tiles.length] ?? (tiles.length > 8 ? "xl:grid-cols-9" : "xl:grid-cols-8");

  return (
    <div role="group" aria-label={ariaLabel} className={`grid grid-cols-2 gap-3 sm:grid-cols-4 ${wide}`}>
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} recalculating={recalculating || tile.recalculating} />
      ))}
    </div>
  );
}
